import { Link } from 'react-router-dom'
import './App.css';
import './index.css';
import Titlebar from './Titlebar';
import TimePeriodSelector from './TimePeriodSelector';
import { TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useState } from 'react';
import { useEffect } from 'react';
import * as FormatFunctions from './FormatFunctions';
import { apiUrl } from './config';
import useUserStore from './UserStore';

function OrderTable() {
    const [checkedItems, setCheckedItems] = useState([]);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState('This Month');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const user = useUserStore((state) => state.user);
    const userEmail = user?.email
        || user?.user?.email
        || user?.profile?.email
        || user?.authResponse?.profile?.email
        || '';

    const rawUserRoles = [
        ...(Array.isArray(user?.roles) ? user.roles : []),
        ...(Array.isArray(user?.user?.roles) ? user.user.roles : []),
        ...(Array.isArray(user?.profile?.roles) ? user.profile.roles : []),
        ...(Array.isArray(user?.profileinfo?.roles) ? user.profileinfo.roles : []),
        ...(Array.isArray(user?.authResponse?.profile?.roles) ? user.authResponse.profile.roles : []),
    ];

    const userRoles = rawUserRoles
        .map((role) => {
            if (typeof role === 'string') {
                return role;
            }
            if (role && typeof role === 'object') {
                return role.name || role.role || role.value || '';
            }
            return '';
        })
        .filter(Boolean);

    const isAdmin = userRoles.some((role) => String(role || '').trim().toLowerCase() === 'admin');

    useEffect(() => {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        setFromDate(firstDayOfMonth.toISOString().split('T')[0]);
        setToDate(lastDayOfMonth.toISOString().split('T')[0]);
    }, []);

    const fetchOrders = async (page = 1, limit = 10) => {
        try {
            setLoading(true);

            if (!isAdmin && !userEmail) {
                setOrders([]);
                setPagination({});
                setError('Unable to determine logged-in user email for order filtering.');
                return;
            }

            const queryParams = new URLSearchParams({
                page: String(page),
                page_size: String(limit),
            });

            if (fromDate) {
                queryParams.set('from_date', fromDate);
            }

            if (toDate) {
                queryParams.set('to_date', toDate);
            }

            if (!isAdmin && userEmail) {
                queryParams.set('sales_rep', userEmail);
            }

            const response = await fetch(apiUrl(`/order/all?${queryParams.toString()}`));

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const orderData = await response.json();
            console.log('Fetched orders:', orderData);

            const fetchedOrders = Array.isArray(orderData?.data) ? orderData.data : [];
            const normalizedUserEmail = (userEmail || '').toString().trim().toLowerCase();

            const scopedOrders = (!isAdmin && normalizedUserEmail)
                ? fetchedOrders.filter((order) => {
                    const salesRepCandidate = (
                        order?.sales_rep
                        || order?.sales_rep_email
                        || order?.salesRepEmail
                        || order?.account_rep
                        || ''
                    ).toString().trim().toLowerCase();
                    return salesRepCandidate === normalizedUserEmail;
                })
                : fetchedOrders;

            setOrders(scopedOrders);
            setPagination(orderData.pagination || {});
                        
            setError(null);
        } catch (error) {
            console.error('Error fetching orders:', error);
            setError('Failed to load orders. Please check if the server is running.');
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(pagination.current_page || 1, pagination.page_size || 10);
    }, [pagination.current_page, pagination.page_size, isAdmin, userEmail, selectedPeriod, fromDate, toDate]);

    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
    };

    const handleFromDateChange = (date) => {
        setFromDate(date);
    };

    const handleToDateChange = (date) => {
        setToDate(date);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== pagination.current_page) {
            fetchOrders(newPage, pagination.page_size);
            setCheckedItems([]); // Clear selections when changing pages
        }
    };

    const handleItemsPerPageChange = (newLimit) => {
        fetchOrders(1, newLimit); // Reset to page 1 when changing items per page
        setCheckedItems([]);
    };

    const deleteOrders = async () => {
        if (checkedItems.length === 0) return;
        
        if (window.confirm(`Are you sure you want to delete ${checkedItems.length} order(s)?`)) {
            try {
                // Delete each selected order using query parameter
                const deletePromises = checkedItems.map(orderId =>
                    fetch(apiUrl(`/order/delete?order_id=${orderId}`), {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        mode: 'cors',
                    })
                );

                const responses = await Promise.all(deletePromises);
                
                // Check if all requests were successful
                responses.forEach((response, index) => {
                    if (!response.ok) {
                        throw new Error(`Failed to delete order ${checkedItems[index]}: ${response.status}`);
                    }
                });
                
                // Refresh the current page after deletion
                fetchOrders(pagination.current_page || 1, pagination.page_size || 10);
                setCheckedItems([]);
                
                console.log('Successfully deleted orders with IDs:', checkedItems);
            } catch (error) {
                console.error('Error deleting orders:', error);
                alert(`Failed to delete orders: ${error.message}`);
            }
        }
    };

    const handleCheckboxChange = (id) => {
        setCheckedItems(checkedItems => {
            if (checkedItems.includes(id)) {
                // If already checked, uncheck it
                return checkedItems.filter(item => item !== id);
            } else {
                // If not checked, check it
                return [...checkedItems, id];
            }
        });
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        const { current_page, total_pages } = pagination;
        
        let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        
        return pages;
    };

    if (loading) {
        return (
            <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
                <Titlebar title="Active Orders"/>
                <div className="m-3 px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500">Loading orders...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
                <Titlebar title="Active Orders"/>
                <div className="m-3 px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center py-12">
                        <div className="text-red-500">{error}</div>
                    </div>
                </div>
            </div>
        );
    }

    const handleViewOrder = async (orderId) => {
        try {
            console.log('Viewing order:', orderId);
            
            const response = await fetch(apiUrl(`/order/byid/${orderId}`), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });

            if (!response.ok) {
                throw new Error(`Failed to load order: ${response.status}`);
            }

            const orderData = await response.json();
            console.log('Order details:', orderData);

            // Navigate to view route
            window.location.href = `/order/view/${orderId}`;
            
        } catch (error) {
            console.error('Error loading order details:', error);
            alert(`Failed to load order details: ${error.message}`);
        }
    };

    const loadOrderForEdit = async (orderId) => {
        try {
            console.log('Loading order for edit:', orderId);
            
            const response = await fetch(apiUrl(`/order/byid/${orderId}`), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });

            if (!response.ok) {
                throw new Error(`Failed to load order: ${response.status}`);
            }

            const orderData = await response.json();
            console.log('Loaded order data:', orderData);

            // Navigate to edit route
            window.location.href = `/orderentry/edit/${orderId}`;
            
        } catch (error) {
            console.error('Error loading order for edit:', error);
            alert(`Failed to load order: ${error.message}`);
        }
    };

    return (
        <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
            <Titlebar title="Active Orders"/>
                
            <div className="m-3 px-4 sm:px-6 lg:px-8">

            {/* Time Period Selector */}
            <div className="flex items-center justify-between mb-6">
            <TimePeriodSelector 
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
            />
            </div>

                <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        {orders && orders.length > 0 && (
                        <p className="mt-2 text-sm text-gray-700">
                            Showing {orders.length} of {pagination.total_count} orders
                        </p>
                        )}
                    </div>
                    <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                        <Link to={`/orderentry/platinum`}
                            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                            Add Order
                        </Link>
                    </div>
                </div>
                
                <div className="mt-8 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            {/* Guard: Only render table if orders exists and is an array */}
                            {orders && Array.isArray(orders) ? (
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead>
                                        <tr>
                                            <th className="w-1.5 px-3 py-3 text-sm font-semibold text-gray-900 sm:table-cell">
                                                {/* Button with trash can icon   */}
                                                <button
                                                    type="button"
                                                    onClick={deleteOrders}
                                                    disabled={!checkedItems.length > 0}
                                                    className="rounded-full bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                                                >
                                                    <TrashIcon aria-hidden="true" className="h-5 w-5" />
                                                </button>
                                            </th>                            
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Customer Name
                                            </th>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                                Order Type
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Start Date
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                End Date
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Total Impressions
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Total Value
                                            </th>
                                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                                                <span className="sr-only">Edit</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {orders.map((order) => (
                                            <tr key={order.id}>
                                                <td className="w-1.5 px-3 py-5 text-sm font-medium text-gray-900 sm:table-cell">
                                                    <input
                                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                        type="checkbox"
                                                        checked={checkedItems.includes(order.id)}
                                                        onChange={() => handleCheckboxChange(order.id)}
                                                    />
                                                </td>
                                                <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
                                                    <div className="flex items-center">
                                                        <div className="ml-4">
                                                            <div className="font-medium text-gray-900">{order.customer_name}</div>
                                                            <div className="mt-1 text-gray-500">{order.phone}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-5 text-sm">
                                                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                                        ((order.order_type || order.orderType || '').toString().trim().toLowerCase() === 'cash')
                                                            ? 'bg-green-50 text-green-700 ring-green-600/20'
                                                            : 'bg-blue-50 text-blue-700 ring-blue-600/20'
                                                    }`}>
                                                        {(() => {
                                                            const orderType = (order.order_type || order.orderType || '').toString().trim().toLowerCase();
                                                            if (orderType === 'cash') return 'Cash';
                                                            if (orderType === 'trade' || orderType === 'trede') return 'Trade';
                                                            return order.order_type || order.orderType || '-';
                                                        })()}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                                                    <span className="inline-flex items-center rounded-md bg-green-50 px-3 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                                        {FormatFunctions.formatDate(order.start_date)}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-4 py-5 text-sm text-gray-500">{FormatFunctions.formatDate(order.end_date)}</td>
                                                <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{order.total_quantity}</td>
                                                <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{FormatFunctions.formatCurrency(order.total_value)}</td>
                                                <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                                                    <button 
                                                        onClick={() => handleViewOrder(order.id)}
                                                        className="text-indigo-600 hover:text-indigo-900 mr-2"
                                                    >
                                                        View<span className="sr-only">, Order {order.id}</span>
                                                    </button>
                                                    &nbsp; | &nbsp; 
                                                    <button 
                                                        onClick={() => loadOrderForEdit(order.id)}
                                                        className="text-indigo-600 hover:text-indigo-900"
                                                    >
                                                        Edit<span className="sr-only">, Order {order.id}</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-gray-500">
                                        {orders === null ? 'No orders data available.' : 'Unable to load orders.'}
                                    </div>
                                </div>
                            )}
                            
                            {/* Show empty state only when orders is an array but empty */}
                            {orders && Array.isArray(orders) && orders.length === 0 && (
                                <div className="text-center py-12">
                                    <div className="text-gray-500">No orders found.</div>
                                </div>
                            )}                  
                        </div>
                    </div>
                </div>

                {/* Pagination Controls */}
                {/* if orders is null or zero length hide pagination */}
                {orders && orders.length > 0 && (pagination.totalPages > 1 || true) && (

                    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                        <div className="flex flex-1 justify-between sm:hidden">
                            <button
                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page <= 1}
                                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page >= pagination.total_pages}
                                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                            <div className="flex items-center space-x-2">
                                <p className="text-sm text-gray-700">
                                    Page <span className="font-medium">{pagination.current_page}</span> of{' '}
                                    <span className="font-medium">{pagination.total_pages}</span>
                                    <span className="text-xs text-gray-500 ml-2">
                                        (Total: {pagination.totalItems} orders)
                                    </span>
                                </p>
                                <select
                                    value={pagination.page_size}
                                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                    className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                    <option value={5}>5 per page</option>
                                    <option value={10}>10 per page</option>
                                    <option value={25}>25 per page</option>
                                    <option value={50}>50 per page</option>
                                </select>
                            </div>
                            <div>
                                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                    <button
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page <= 1}
                                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Previous</span>
                                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                    
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                                pageNum === pagination.current_page
                                                    ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                    
                                    <button
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page >= pagination.total_pages}
                                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Next</span>
                                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
};

export default OrderTable;